import { useEffect, useSyncExternalStore } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { apiUrl, getAuthToken } from '@/lib/axios';
import {
  getMessagesCharId,
  markConversationRead,
  messagesThreadQueryKey,
  refreshMessagesUnreadCount,
  scheduleConversationsSync,
  upsertConversationInCache,
  type ChatMessage,
} from '@/hooks/useMessages';
import { sortMessagesChronologically } from '@/pages/messages/messageUtils';
import { messageBelongsToThread } from '@/pages/messages/messageBelongsToThread';

export type MessagesWsEvent =
  | { type: 'connected' }
  | { type: 'disconnected' }
  | { type: 'auth_success'; charID: number }
  | { type: 'new_message'; data: Record<string, unknown> }
  | { type: 'typing'; sender: number }
  | { type: 'user_status'; charID: number; status: 'online' | 'offline' }
  | { type: 'refresh_unread' };

type Listener = (event: MessagesWsEvent) => void;

let ws: WebSocket | null = null;
let reconnectAttempts = 0;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let pingTimer: ReturnType<typeof setInterval> | null = null;
let activeCharId = 0;
let viewingOtherCharId: number | null = null;
const listeners = new Set<Listener>();
const statusListeners = new Set<() => void>();

const MAX_RECONNECT = 10;
const RECONNECT_DELAY_MS = 3000;

function getWsUrl(token: string): string {
  const path = '/ws/messages';
  const encoded = encodeURIComponent(token);

  if (import.meta.env.DEV) {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${window.location.host}${path}?token=${encoded}`;
  }

  if (apiUrl) {
    const base = apiUrl.replace(/^http/i, (m) => (m.toLowerCase() === 'https' ? 'wss' : 'ws'));
    return `${base}${path}?token=${encoded}`;
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}${path}?token=${encoded}`;
}

function resolveCharId(): number {
  return getMessagesCharId();
}

function notify(event: MessagesWsEvent) {
  for (const listener of listeners) listener(event);
}

function notifyStatusChange() {
  for (const listener of statusListeners) listener();
}

function connectionState(): boolean {
  return ws?.readyState === WebSocket.OPEN;
}

function subscribeStatus(listener: () => void) {
  statusListeners.add(listener);
  return () => statusListeners.delete(listener);
}

function clearTimers() {
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (pingTimer) {
    clearInterval(pingTimer);
    pingTimer = null;
  }
}

function scheduleReconnect() {
  if (reconnectAttempts >= MAX_RECONNECT) return;
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => {
    connectMessagesWebSocket(activeCharId);
  }, RECONNECT_DELAY_MS);
}

export function connectMessagesWebSocket(charId: number) {
  if (charId <= 0) return;
  activeCharId = charId;

  const token = getAuthToken();
  if (!token) return;

  if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
    return;
  }

  clearTimers();

  try {
    ws = new WebSocket(getWsUrl(token));

    ws.onopen = () => {
      reconnectAttempts = 0;
      notify({ type: 'connected' });
      notifyStatusChange();

      pingTimer = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: 'ping' }));
        }
      }, 30_000);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(String(event.data)) as Record<string, unknown>;
        handleWsPayload(data);
      } catch {
        // ignore malformed payloads
      }
    };

    ws.onclose = () => {
      notify({ type: 'disconnected' });
      notifyStatusChange();
      clearTimers();
      ws = null;
      scheduleReconnect();
    };

    ws.onerror = () => {
      ws?.close();
    };
  } catch {
    scheduleReconnect();
  }
}

export function disconnectMessagesWebSocket() {
  clearTimers();
  reconnectAttempts = MAX_RECONNECT;
  ws?.close();
  ws = null;
  notifyStatusChange();
}

function handleWsPayload(data: Record<string, unknown>) {
  switch (data.type) {
    case 'auth_success':
      notify({ type: 'auth_success', charID: Number(data.charID) });
      break;
    case 'new_message':
      notify({ type: 'new_message', data });
      break;
    case 'typing':
      notify({ type: 'typing', sender: Number(data.sender) });
      break;
    case 'user_status':
      notify({
        type: 'user_status',
        charID: Number(data.charID),
        status: data.status === 'online' ? 'online' : 'offline',
      });
      break;
    case 'refresh_unread':
      notify({ type: 'refresh_unread' });
      break;
    default:
      break;
  }
}

export function sendWsPayload(payload: Record<string, unknown>) {
  if (ws?.readyState === WebSocket.OPEN) {
    ws.send(JSON.stringify(payload));
  }
}

export function sendTyping(receiver: number) {
  sendWsPayload({ type: 'typing', receiver });
}

export function checkUserStatus(charId: number) {
  sendWsPayload({ type: 'check_status', charID: charId });
}

export function subscribeMessagesWs(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function setViewingConversation(otherCharId: number | null) {
  viewingOtherCharId = otherCharId && otherCharId > 0 ? otherCharId : null;
}

export function getViewingConversation(): number | null {
  return viewingOtherCharId;
}

function appendIncomingToThread(
  queryClient: QueryClient,
  charId: number,
  other: number,
  data: Record<string, unknown>,
  sender: number,
  receiver: number,
) {
  queryClient.setQueryData<ChatMessage[]>(messagesThreadQueryKey(charId, other), (prev) => {
    const list = prev ?? [];
    const id = Number(data.id);
    if (list.some((m) => m.id === id)) return list;
    const isViewing = viewingOtherCharId === other;
    const row: ChatMessage = {
      id,
      sender_CharID: sender,
      receiver_CharID: receiver,
      id64: data.id64 ? Number(data.id64) : null,
      message: String(data.message ?? ''),
      status: isViewing && receiver === charId ? 'read' : String(data.status ?? 'unread'),
      created_at: String(data.timestamp ?? data.created_at ?? ''),
      senderName: '',
      senderRefObjID: 0,
      receiverName: '',
      receiverRefObjID: 0,
      itemName: data.itemName ? String(data.itemName) : null,
      itemIcon: data.itemIcon ? String(data.itemIcon) : undefined,
      isLocked: 0,
    };
    if (!messageBelongsToThread(row, charId, other)) return list;
    return sortMessagesChronologically([...list, row]);
  });
}

function handleNewMessageEvent(
  queryClient: QueryClient,
  charId: number,
  data: Record<string, unknown>,
) {
  const sender = Number(data.sender);
  const receiver = Number(data.receiver);
  const other = sender === charId ? receiver : receiver === charId ? sender : null;
  if (!other) {
    void refreshMessagesUnreadCount(queryClient, charId);
    return;
  }

  const isIncoming = receiver === charId;
  const isViewing = viewingOtherCharId === other;
  const wasNew = upsertConversationInCache(queryClient, charId, {
    otherCharID: other,
    otherCharName: String(data.senderName ?? data.otherCharName ?? ''),
    otherRefObjID: Number(data.senderRefObjID ?? data.otherRefObjID ?? 0),
    message: String(data.message ?? ''),
    created_at: String(data.timestamp ?? data.created_at ?? ''),
    senderCharID: sender,
    unreadDelta: isIncoming && !isViewing ? 1 : 0,
  });
  scheduleConversationsSync(queryClient, charId, wasNew ? 1200 : 1500);

  if (isViewing) {
    appendIncomingToThread(queryClient, charId, other, data, sender, receiver);
    if (data.id64) void queryClient.invalidateQueries({queryKey: messagesThreadQueryKey(charId, other)});
    if (isIncoming) {
      void markConversationRead(queryClient, charId, other);
      return;
    }
  }

  void refreshMessagesUnreadCount(queryClient, charId);
}

export function processMessagesWsEvent(
  queryClient: QueryClient,
  charId: number,
  event: MessagesWsEvent,
) {
  if (event.type === 'refresh_unread') {
    void refreshMessagesUnreadCount(queryClient, charId);
    scheduleConversationsSync(queryClient, charId);
    return;
  }

  if (event.type === 'new_message') {
    handleNewMessageEvent(queryClient, charId, event.data);
  }
}

export function useMessagesWebSocketConnected(): boolean {
  return useSyncExternalStore(subscribeStatus, connectionState, () => false);
}

export function useMessagesWebSocketSync(activeOtherCharId: number | null, charIdOverride?: number) {
  const charId = charIdOverride ?? resolveCharId();

  useEffect(() => {
    if (charId <= 0) return;
    connectMessagesWebSocket(charId);
  }, [charId]);

  useEffect(() => {
    setViewingConversation(activeOtherCharId);
    return () => setViewingConversation(null);
  }, [activeOtherCharId]);
}
